// Утилита для сбора технической информации о клиенте
import { getGoogleClientID, getYandexClientID } from "./analytics-client-id"

export interface ClientInfo {
  browser?: string
  os?: string
  device?: string
  screen?: string
  language?: string
  timezone?: string
  referrer?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  yandex_client_id?: string
  google_client_id?: string
}

/**
 * Определяет браузер пользователя
 */
function getBrowser(): string {
  if (typeof window === "undefined") return "Unknown"

  const ua = navigator.userAgent

  if (ua.includes("Firefox/")) {
    const version = ua.match(/Firefox\/([\d.]+)/)?.[1]
    return `Firefox ${version || ""}`
  }
  if (ua.includes("Edg/")) {
    const version = ua.match(/Edg\/([\d.]+)/)?.[1]
    return `Edge ${version || ""}`
  }
  if (ua.includes("Chrome/") && !ua.includes("Edg/")) {
    const version = ua.match(/Chrome\/([\d.]+)/)?.[1]
    return `Chrome ${version || ""}`
  }
  if (ua.includes("Safari/") && !ua.includes("Chrome/")) {
    const version = ua.match(/Version\/([\d.]+)/)?.[1]
    return `Safari ${version || ""}`
  }
  if (ua.includes("Opera/") || ua.includes("OPR/")) {
    const version = ua.match(/(?:Opera|OPR)\/([\d.]+)/)?.[1]
    return `Opera ${version || ""}`
  }

  return "Unknown"
}

/**
 * Определяет операционную систему
 */
function getOS(): string {
  if (typeof window === "undefined") return "Unknown"

  const ua = navigator.userAgent
  const platform = navigator.platform

  if (ua.includes("Win")) return "Windows"
  if (ua.includes("Mac")) return "macOS"
  if (ua.includes("Linux")) return "Linux"
  if (ua.includes("Android")) return "Android"
  if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS"

  return platform || "Unknown"
}

/**
 * Определяет тип устройства
 */
function getDevice(): string {
  if (typeof window === "undefined") return "Unknown"

  const ua = navigator.userAgent

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "Tablet"
  }
  if (
    /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua,
    )
  ) {
    return "Mobile"
  }

  return "Desktop"
}

/**
 * Получает разрешение экрана
 */
function getScreen(): string {
  if (typeof window === "undefined") return "Unknown"

  return `${window.screen.width}x${window.screen.height}`
}

/**
 * Получает язык браузера
 */
function getLanguage(): string {
  if (typeof window === "undefined") return "Unknown"

  return navigator.language || "Unknown"
}

/**
 * Получает часовой пояс
 */
function getTimezone(): string {
  if (typeof window === "undefined") return "Unknown"

  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Паттерны для определения источника трафика
const REFERRER_PATTERNS = new Map([
  ["google", "Google"],
  ["yandex", "Yandex"],
  ["facebook", "Facebook"],
  ["fb.", "Facebook"],
  ["instagram", "Instagram"],
  ["vk.com", "VK"],
  ["vkontakte", "VK"],
  ["youtube", "YouTube"],
  ["linkedin", "LinkedIn"],
  ["twitter", "Twitter/X"],
  ["t.co", "Twitter/X"],
  ["telegram", "Telegram"],
])

/**
 * Получает источник перехода
 */
function getReferrer(): string {
  if (typeof window === "undefined") return "Unknown"

  if (!document.referrer) {
    return "Прямой переход"
  }

  try {
    const referrerUrl = new URL(document.referrer)
    const currentUrl = new URL(window.location.href)

    if (referrerUrl.hostname === currentUrl.hostname) {
      return "Внутренний переход"
    }

    const hostname = referrerUrl.hostname.toLowerCase()

    // Проверяем паттерны
    for (const [pattern, name] of REFERRER_PATTERNS) {
      if (hostname.includes(pattern)) {
        return name
      }
    }

    return referrerUrl.hostname
  } catch {
    return document.referrer
  }
}

/**
 * Извлекает UTM метки из URL
 */
function getUTMParams(): Pick<
  ClientInfo,
  "utm_source" | "utm_medium" | "utm_campaign" | "utm_content" | "utm_term"
> {
  if (typeof window === "undefined") return {}

  const params = new URLSearchParams(window.location.search)

  return {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_content: params.get("utm_content") || undefined,
    utm_term: params.get("utm_term") || undefined,
  }
}

/**
 * Собирает всю техническую информацию о клиенте
 */
export async function getClientInfo(): Promise<ClientInfo> {
  const [yandexClientId, googleClientId] = await Promise.all([
    getYandexClientID(),
    getGoogleClientID(),
  ])

  return {
    browser: getBrowser(),
    os: getOS(),
    device: getDevice(),
    screen: getScreen(),
    language: getLanguage(),
    timezone: getTimezone(),
    referrer: getReferrer(),
    ...getUTMParamsWithFallback(),
    yandex_client_id: yandexClientId || undefined,
    google_client_id: googleClientId || undefined,
  }
}

/**
 * Сохраняет UTM метки в sessionStorage для использования на других страницах
 */
export function saveUTMParams(): void {
  if (typeof window === "undefined") return

  const utmParams = getUTMParams()
  const hasUTM = Object.values(utmParams).some(Boolean)

  if (hasUTM) {
    sessionStorage.setItem("utm_params", JSON.stringify(utmParams))
  }
}

/**
 * Получает сохраненные UTM метки из sessionStorage
 */
export function getSavedUTMParams(): Pick<
  ClientInfo,
  "utm_source" | "utm_medium" | "utm_campaign" | "utm_content" | "utm_term"
> {
  if (typeof window === "undefined") return {}

  try {
    const saved = sessionStorage.getItem("utm_params")
    return saved ? JSON.parse(saved) : {}
  } catch {
    return {}
  }
}

/**
 * Получает UTM метки: сначала из URL, затем из sessionStorage
 */
export function getUTMParamsWithFallback(): Pick<
  ClientInfo,
  "utm_source" | "utm_medium" | "utm_campaign" | "utm_content" | "utm_term"
> {
  const currentUTM = getUTMParams()
  const hasCurrentUTM = Object.values(currentUTM).some(Boolean)

  if (hasCurrentUTM) {
    return currentUTM
  }

  return getSavedUTMParams()
}
